import { Button, Divider, Flex, Text } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { parseBEDateIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date';
import {
	GetInvestorTransactionDetailsRes,
	VestingTermSimplifiedRes,
} from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	TransactionVestingTermFormValues,
	TransactionVestingTermShape,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/TransactionVestingTermForm.types.ts';
import {
	getTransactionTypeLabel,
	getVestingTermName,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/transactionTable.utils.ts';
import { getStartDateError } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/TransactionVestingTermForm.utils.ts';
import { VestingTermFormStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/HoldingTransactionsModal.styles.tsx';
import { useTransactionVestingTerm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/HoldingTransactionsModal/useTransactionVestingTerm.ts';

interface Props {
	transaction: GetInvestorTransactionDetailsRes;
	vestingTerms: VestingTermSimplifiedRes[];
	onClose: () => void;
}

export const TransactionVestingTermForm = ({ transaction, vestingTerms, onClose }: Props) => {
	const tMipManage = useTranslationMipManage();
	const removeOnly = !transaction.isIssue;
	const { save, remove } = useTransactionVestingTerm(transaction.id);

	const form = useForm<TransactionVestingTermFormValues>({
		resolver: zodResolver(TransactionVestingTermShape),
		defaultValues: {
			vestingTermId: transaction.vestingTermId ?? null,
			vestingStartDate: transaction.vestingStartDate
				? parseBEDateIgnoreTimezone(transaction.vestingStartDate)
				: null,
		},
	});

	const { isDirty } = form.formState;
	const hasVestingTerm = !!form.watch('vestingTermId');
	const isPending = save.isPending || remove.isPending;

	const onSuccess = () => {
		showToastV2({ title: tMipManage('investors.success'), state: 'success' });
		onClose();
	};

	const onSubmit = ({ vestingTermId, vestingStartDate }: TransactionVestingTermFormValues) => {
		if (!vestingTermId) {
			return;
		}

		save.mutate(
			{ vestingTermId, vestingStartDate },
			{
				onSuccess,
				onError: (error) => {
					const startDateError = getStartDateError(error);
					if (startDateError) {
						form.setError('vestingStartDate', { message: startDateError });
					}
				},
			}
		);
	};

	return (
		<FormProvider {...form}>
			<VestingTermFormStyles
				onSubmit={form.handleSubmit(onSubmit)}
				data-testid={
					TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_FORM
				}
			>
				<div className="panel">
					<Flex direction="column" gap="0.5rem">
						<Flex justify="space-between" align="center">
							<Text fz={14} fw={600} c="gray.9">
								{tMipManage(
									'investors.details.holding_transactions.edit_vesting.title'
								)}
							</Text>

							<Text fz={12} c="dark.5">
								{tMipManage(
									'investors.details.holding_transactions.edit_vesting.context',
									{
										type:
											getTransactionTypeLabel(
												tMipManage,
												transaction.transactionType
											) ?? '',
										units: formatNumber({
											value: transaction.totalVolume ?? 0,
										}),
									}
								)}
							</Text>
						</Flex>

						<Divider />
					</Flex>

					{removeOnly ? (
						<Flex direction="column" gap="0.25rem" mt="0.5rem">
							<Text fz={14} c="gray.9">
								{getVestingTermName(vestingTerms, transaction)}
							</Text>

							<Text fz={12} c="dark.5">
								{tMipManage(
									'investors.details.holding_transactions.edit_vesting.transfer_remove_only'
								)}
							</Text>
						</Flex>
					) : (
						<Flex gap="1rem" mt="0.5rem">
							<Select
								name="vestingTermId"
								flex={1}
								size="md"
								label={tMipManage('investors.expanded_row.vesting_term')}
								placeholder={tMipManage('investors.select')}
								data={vestingTerms.map((vestingTerm) => ({
									label: vestingTerm.name,
									value: vestingTerm.id,
								}))}
								rightSection={<Icon variant="caret-down" size="lg" />}
								rightSectionProps={{ style: { pointerEvents: 'none' } }}
								disabled={isPending}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_TERM_SELECT
								}
							/>

							<MantineDateInput
								name="vestingStartDate"
								flex={1}
								size="md"
								label={tMipManage('vesting_terms_drawer.vesting_start_date_label')}
								placeholder={tMipManage('investors.select')}
								rightSection={<Icon variant="calendar-outlined" size="lg" />}
								clearable
								disabled={isPending || !hasVestingTerm}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VESTING_START_DATE_INPUT
								}
							/>
						</Flex>
					)}

					<Flex justify="space-between" align="center" mt="0.75rem">
						<Flex gap="0.5rem">
							{!removeOnly && (
								<Button
									type="submit"
									fw={600}
									disabled={!isDirty || !hasVestingTerm || remove.isPending}
									loading={save.isPending}
									data-testid={
										TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_SAVE
									}
								>
									{tMipManage(
										'investors.details.holding_transactions.edit_vesting.save'
									)}
								</Button>
							)}

							<Button
								variant="default"
								fw={600}
								disabled={isPending}
								onClick={onClose}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_CANCEL
								}
							>
								{tMipManage(
									'investors.details.holding_transactions.edit_vesting.cancel'
								)}
							</Button>
						</Flex>

						{!!transaction.vestingTermId && (
							<Button
								variant="subtle"
								color="red"
								fw={600}
								disabled={save.isPending}
								loading={remove.isPending}
								onClick={() => remove.mutate(undefined, { onSuccess })}
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_HOLDING_TRANSACTION_VESTING_REMOVE
								}
							>
								{tMipManage(
									'investors.details.holding_transactions.edit_vesting.remove'
								)}
							</Button>
						)}
					</Flex>
				</div>
			</VestingTermFormStyles>
		</FormProvider>
	);
};
